import { useEffect, useMemo, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { DecimalInput } from '@/components/ui/DecimalInput'
import type { FamilyEditorSchemaDto } from './types'
import type { FamilyEditorSidebarContentProps } from './FamilyEditorSidebarContentFallback'
import { FamilyEditorPricingSection } from './FamilyEditorPricingSection'
import { getQuoteVersionDefaultAttributesV2 } from '@/components/features/project-detail/quotes/quotes-v2.api'
import { CarcassFinishSection } from '@/components/features/elements/CarcassFinishSection'
import { FacadeFinishSection } from '@/components/features/elements/FacadeFinishSection'
import { HandlesFinishSection } from '@/components/features/elements/HandlesFinishSection'
import { PlinthFinishSection } from '@/components/features/elements/PlinthFinishSection'
import { parseDecimalInput } from '@/lib/decimal'

type Attrs = Record<string, unknown>

type SchemaField = FamilyEditorSchemaDto['fields'][number]

function readBool(attrs: Attrs, key: string, fallback: boolean) {
  const v = attrs[key]
  return typeof v === 'boolean' ? v : fallback
}

function readString(attrs: Attrs, key: string, fallback = '') {
  const v = attrs[key]
  return typeof v === 'string' ? v : v == null ? fallback : String(v)
}

function readNumber(attrs: Attrs, key: string): number | null {
  const v = attrs[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    return parseDecimalInput(v)
  }
  return null
}

function ModeBadge({ mode }: { mode: 'default' | 'custom' }) {
  const label = mode === 'default' ? 'Base (devis)' : 'Spécifique'
  const className =
    mode === 'default'
      ? 'rounded-full border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'
      : 'rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary'

  return <span className={className}>{label}</span>
}

function readValue(values: Record<string, unknown>, fieldId: string): unknown {
  return values[fieldId]
}

function readStringField(values: Record<string, unknown>, fieldId: string): string {
  const v = readValue(values, fieldId)
  return typeof v === 'string' ? v : v == null ? '' : String(v)
}

function readBoolField(values: Record<string, unknown>, fieldId: string, fallback: boolean): boolean {
  const v = readValue(values, fieldId)
  return typeof v === 'boolean' ? v : fallback
}

function readNumberField(values: Record<string, unknown>, fieldId: string): number | null {
  const v = readValue(values, fieldId)
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    return parseDecimalInput(v)
  }
  return null
}

function formatFacadePreview(values: Record<string, unknown>, prefix: 'doorFinish' | 'drawerFinish'): string {
  const material = readStringField(values, `${prefix}Material`)
  const color = readStringField(values, `${prefix}Color`)
  if (!material && !color) return ''
  return [material, color].filter(Boolean).join(' - ')
}

function formatCarcassPreview(values: Record<string, unknown>): string {
  const finish = readStringField(values, 'carcassFinishFinish')
  const color = readStringField(values, 'carcassFinishColor')
  if (!finish && !color) return ''
  return [finish, color].filter(Boolean).join(' - ')
}

function formatHandlesPreview(values: Record<string, unknown>): string {
  const model = readStringField(values, 'handlesFinishModel')
  const color = readStringField(values, 'handlesFinishColor')
  if (!model && !color) return ''
  return [model, color].filter(Boolean).join(' - ')
}

function formatPlinthPreviewFromFields(
  materialSameAsDoor: boolean,
  material: string,
  colorSameAsDoor: boolean,
  color: string,
  height: number | null
): string {
  const mat = materialSameAsDoor ? 'Identique aux portes' : material
  const col = colorSameAsDoor ? 'Identique aux portes' : color
  const h = typeof height === 'number' && Number.isFinite(height) ? `${height}cm` : ''
  const parts = [mat, col, h].filter((x) => typeof x === 'string' && x.trim().length > 0)
  return parts.join(' - ')
}

function getField(schema: FamilyEditorSchemaDto | null, fieldId: string): SchemaField | null {
  if (!schema) return null
  const found = (schema.fields || []).find((f) => f.id === fieldId)
  return found ?? null
}

function hasField(schema: FamilyEditorSchemaDto | null, fieldId: string): boolean {
  return !!getField(schema, fieldId)
}

function hasFieldPrefix(schema: FamilyEditorSchemaDto | null, prefix: string): boolean {
  if (!schema) return false
  return (schema.fields || []).some((f) => String(f.id).startsWith(prefix))
}

export function FamilyEditorSidebarContentFurniture({
  projectId,
  quoteId,
  versionId,
  schema,
  isLoading,
  error,
  missingRequiredFields: _missingRequiredFields,
  missingRequiredFieldIds,
  values,
  onChangeValues,
  isFieldRequired: _isFieldRequired,
  quantity,
  onQuantityChange,
  unitPrice,
  onUnitPriceChange,
  vatPercent,
  onVatPercentChange,
  priceIncluded,
  onPriceIncludedChange,
}: FamilyEditorSidebarContentProps) {
  const [defaultsAttrs, setDefaultsAttrs] = useState<Attrs | null>(null)
  const [defaultsLoading, setDefaultsLoading] = useState(false)
  const [isCarcassDialogOpen, setIsCarcassDialogOpen] = useState(false)
  const [isDoorDialogOpen, setIsDoorDialogOpen] = useState(false)
  const [isDrawerDialogOpen, setIsDrawerDialogOpen] = useState(false)
  const [isHandlesDialogOpen, setIsHandlesDialogOpen] = useState(false)
  const [isPlinthDialogOpen, setIsPlinthDialogOpen] = useState(false)

  const isMissing = (fieldId: string) => (effectiveMissingRequiredFieldIds || []).includes(fieldId)

  const setFieldValue = (fieldId: string, next: unknown) => {
    onChangeValues((prev) => ({ ...prev, [fieldId]: next }))
  }

  // Visible only on dedicated corner products (e.g. base_unit_standard_corner) where the
  // schema declares `rightWidth`. Phase 3l corner-products refactor removed the legacy
  // `isCorner` toggle: corner-ness is now implicit in the product code.
  const showRightWidth = hasField(schema, 'rightWidth')
  const doorsIncluded = readBoolField(values, 'doorsIncluded', false)
  const drawersIncluded = readBoolField(values, 'drawersIncluded', false)
  const handlesIncluded = readBoolField(values, 'handlesIncluded', false)
  const plinthIncluded = hasField(schema, 'plinthIncluded') ? readBoolField(values, 'plinthIncluded', false) : false
  const interiorDrawersIncluded = readBoolField(values, 'interiorDrawersIncluded', false)
  const shelvesIncluded = readBoolField(values, 'shelvesIncluded', false)

  const hasDefaultCarcassFinish = values.hasDefaultCarcassFinish !== false
  const hasDefaultDoorFinish = values.hasDefaultDoorFinish !== false
  const hasDefaultDrawerFinish = values.hasDefaultDrawerFinish !== false
  const hasDefaultHandlesFinish = values.hasDefaultHandlesFinish !== false
  const hasDefaultPlinthFinish = values.hasDefaultPlinthFinish !== false

  const effectiveMissingRequiredFieldIds = useMemo(() => {
    if (hasDefaultPlinthFinish) {
      return (missingRequiredFieldIds || []).filter((id) => !String(id).startsWith('plinthCustom'))
    }
    return missingRequiredFieldIds || []
  }, [hasDefaultPlinthFinish, missingRequiredFieldIds])

  const showPlinth =
    hasField(schema, 'hasDefaultPlinthFinish') ||
    hasFieldPrefix(schema, 'plinthCustom.') ||
    hasField(schema, 'plinthCustom')

  useEffect(() => {
    if (!schema) return

    onChangeValues((prev) => {
      const next = { ...prev }

      const ensureBool = (key: string, fallback: boolean) => {
        const v = next[key]
        if (typeof v !== 'boolean') next[key] = fallback
      }

      ensureBool('doorsIncluded', typeof next.doorsIncluded === 'boolean' ? (next.doorsIncluded as boolean) : true)
      ensureBool('handlesIncluded', typeof next.handlesIncluded === 'boolean' ? (next.handlesIncluded as boolean) : true)
      ensureBool('drawersIncluded', typeof next.drawersIncluded === 'boolean' ? (next.drawersIncluded as boolean) : false)
      ensureBool('shelvesIncluded', typeof next.shelvesIncluded === 'boolean' ? (next.shelvesIncluded as boolean) : false)
      if (hasField(schema, 'plinthIncluded')) ensureBool('plinthIncluded', typeof next.plinthIncluded === 'boolean' ? (next.plinthIncluded as boolean) : true)
      ensureBool(
        'interiorDrawersIncluded',
        typeof next.interiorDrawersIncluded === 'boolean' ? (next.interiorDrawersIncluded as boolean) : false
      )

      if (hasField(schema, 'hasDefaultCarcassFinish')) ensureBool('hasDefaultCarcassFinish', true)
      if (hasField(schema, 'hasDefaultDoorFinish')) ensureBool('hasDefaultDoorFinish', true)
      if (hasField(schema, 'hasDefaultDrawerFinish')) ensureBool('hasDefaultDrawerFinish', true)
      if (hasField(schema, 'hasDefaultHandlesFinish')) ensureBool('hasDefaultHandlesFinish', true)
      if (hasField(schema, 'hasDefaultPlinthFinish')) ensureBool('hasDefaultPlinthFinish', true)

      if (next['depth'] == null && readValue(next, 'depth') == null) {
        next['depth'] = 57.5
      }

      // Strip stale `isCorner` (legacy attribute, never in new schemas) and stale
      // `rightWidth` if the current product is not a corner variant.
      if (next.isCorner !== undefined) next.isCorner = undefined
      if (!hasField(schema, 'rightWidth') && next.rightWidth !== undefined) {
        next.rightWidth = undefined
      }

      return next
    })
  }, [schema, onChangeValues])

  useEffect(() => {
    if (!quoteId || !versionId) return

    let isMounted = true

    const load = async () => {
      try {
        setDefaultsLoading(true)
        const response = await getQuoteVersionDefaultAttributesV2(projectId, quoteId, versionId)
        if (!isMounted) return
        setDefaultsAttrs(response.attributes)
      } catch {
        if (!isMounted) return
        setDefaultsAttrs(null)
      } finally {
        if (isMounted) setDefaultsLoading(false)
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [projectId, quoteId, versionId])

  const defaultsFacade = useMemo(() => {
    if (!defaultsAttrs) return null
    return {
      material: readString(defaultsAttrs, 'facadeMaterial', ''),
      color: readString(defaultsAttrs, 'facadeColor', ''),
      edgeColorSameAsDoor: readBool(defaultsAttrs, 'facadeEdgeColorSameAsDoor', true),
      edgeColor: readString(defaultsAttrs, 'facadeEdgeColor', ''),
    }
  }, [defaultsAttrs])

  const defaultsCarcass = useMemo(() => {
    if (!defaultsAttrs) return null
    return {
      finish: readString(defaultsAttrs, 'carcassFinish', ''),
      carcassColor: readString(defaultsAttrs, 'carcassColor', ''),
      edgeColorSameAsDoor: readBool(defaultsAttrs, 'carcassEdgeColorSameAsDoor', true),
      edgeColor: readString(defaultsAttrs, 'carcassEdgeColor', ''),
      sideViewFinishSameAsDoor: readBool(defaultsAttrs, 'carcassSideViewFinishSameAsDoor', true),
      sideViewFinish: readString(defaultsAttrs, 'carcassSideViewFinish', ''),
    }
  }, [defaultsAttrs])

  const defaultsHandles = useMemo(() => {
    if (!defaultsAttrs) return null
    return {
      model: readString(defaultsAttrs, 'handlesModel', ''),
      color: readString(defaultsAttrs, 'handlesColor', ''),
    }
  }, [defaultsAttrs])

  const defaultsPlinth = useMemo(() => {
    if (!defaultsAttrs) return null
    return {
      materialSameAsDoor: false,
      material: readString(defaultsAttrs, 'plinthMaterial', ''),
      colorSameAsDoor: readBool(defaultsAttrs, 'plinthColorSameAsDoor', true),
      color: readString(defaultsAttrs, 'plinthColor', ''),
      height: readNumber(defaultsAttrs, 'plinthHeight') ?? undefined,
    }
  }, [defaultsAttrs])

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      {isLoading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Chargement...</div>
      ) : !schema ? (
        error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : null
      ) : (
        <div className="space-y-3">
          <div className="space-y-6">
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            ) : null}

            <div className="rounded-md border p-3">
              <div className="text-xs font-medium text-muted-foreground">Général</div>
              <div className="mt-2 grid grid-cols-1 gap-y-2 md:grid-cols-3 md:gap-x-6">
                {showPlinth ? (
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox
                      id="fes-furniture-plinthIncluded"
                      checked={plinthIncluded}
                      onCheckedChange={(v) => setFieldValue('plinthIncluded', v === true)}
                    />
                    <Label htmlFor="fes-furniture-plinthIncluded" className="text-sm">
                      Socle
                    </Label>
                  </div>
                ) : null}

                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id="fes-furniture-doorsIncluded"
                    checked={doorsIncluded}
                    onCheckedChange={(v) => setFieldValue('doorsIncluded', v === true)}
                  />
                  <Label htmlFor="fes-furniture-doorsIncluded" className="text-sm">
                    Portes
                  </Label>
                </div>

                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id="fes-furniture-drawersIncluded"
                    checked={drawersIncluded}
                    onCheckedChange={(v) => setFieldValue('drawersIncluded', v === true)}
                  />
                  <Label htmlFor="fes-furniture-drawersIncluded" className="text-sm">
                    Tiroirs
                  </Label>
                </div>

                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id="fes-furniture-shelvesIncluded"
                    checked={shelvesIncluded}
                    onCheckedChange={(v) => setFieldValue('shelvesIncluded', v === true)}
                  />
                  <Label htmlFor="fes-furniture-shelvesIncluded" className="text-sm">
                    Étagères
                  </Label>
                </div>

                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id="fes-furniture-interiorDrawersIncluded"
                    checked={interiorDrawersIncluded}
                    onCheckedChange={(v) => setFieldValue('interiorDrawersIncluded', v === true)}
                  />
                  <Label htmlFor="fes-furniture-interiorDrawersIncluded" className="text-sm">
                    Tiroirs intérieurs
                  </Label>
                </div>

                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id="fes-furniture-handlesIncluded"
                    checked={handlesIncluded}
                    onCheckedChange={(v) => setFieldValue('handlesIncluded', v === true)}
                  />
                  <Label htmlFor="fes-furniture-handlesIncluded" className="text-sm">
                    Poignées
                  </Label>
                </div>
              </div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs font-medium text-muted-foreground">Dimensions</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3">
                <div className="space-y-1">
                  <Label className="text-sm">Largeur (cm)</Label>
                  <DecimalInput
                    className="h-9"
                    value={typeof values.width === 'string' ? values.width : values.width == null ? '' : String(values.width)}
                    onValueChange={(next) => setFieldValue('width', next === '' ? undefined : next)}
                    allowNegative={false}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-sm">Hauteur (cm)</Label>
                  <DecimalInput
                    className="h-9"
                    value={typeof values.height === 'string' ? values.height : values.height == null ? '' : String(values.height)}
                    onValueChange={(next) => setFieldValue('height', next === '' ? undefined : next)}
                    allowNegative={false}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-sm">Profondeur (cm)</Label>
                  <DecimalInput
                    className="h-9"
                    value={typeof values.depth === 'string' ? values.depth : values.depth == null ? '' : String(values.depth)}
                    onValueChange={(next) => setFieldValue('depth', next === '' ? undefined : next)}
                    allowNegative={false}
                  />
                </div>

                {showRightWidth ? (
                  <div className="space-y-1">
                    <Label className="text-sm">Largeur droite (cm)</Label>
                    <DecimalInput
                      className={'h-9 ' + (isMissing('rightWidth') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={typeof values.rightWidth === 'string' ? values.rightWidth : values.rightWidth == null ? '' : String(values.rightWidth)}
                      onValueChange={(next) => setFieldValue('rightWidth', next === '' ? undefined : next)}
                      allowNegative={false}
                    />
                    {isMissing('rightWidth') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>
                ) : null}
              </div>
            </div>

            {doorsIncluded ? (
              <div className="rounded-md border p-3">
                <div className="text-xs font-medium text-muted-foreground">Portes</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Nombre de portes</Label>
                    <DecimalInput
                      className={'h-8 ' + (isMissing('doorCount') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={typeof values.doorCount === 'string' ? values.doorCount : values.doorCount == null ? '' : String(values.doorCount)}
                      onValueChange={(next) => setFieldValue('doorCount', next === '' ? undefined : next)}
                      allowNegative={false}
                    />
                    {isMissing('doorCount') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-muted-foreground">Finition</div>
                      <ModeBadge mode={hasDefaultDoorFinish ? 'default' : 'custom'} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {defaultsLoading ? (
                        'Chargement des finitions de base…'
                      ) : hasDefaultDoorFinish ? (
                        defaultsFacade ? `${defaultsFacade.material || ''}${defaultsFacade.color ? ` - ${defaultsFacade.color}` : ''}` :
                        'Finition de base (devis)'
                      ) : (
                        formatFacadePreview(values, 'doorFinish') || 'Spécifique'
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => {
                        setIsDoorDialogOpen(true)
                      }}
                    >
                      Modifier…
                    </Button>
                  </div>
                </div>

                <FacadeFinishSection
                  generalFinish={defaultsFacade ?? undefined}
                  useGeneral={hasDefaultDoorFinish}
                  customMaterial={readStringField(values, 'doorFinishMaterial')}
                  customColor={readStringField(values, 'doorFinishColor')}
                  customEdgeColor={readStringField(values, 'doorFinishEdgeColor')}
                  customEdgeColorSameAsDoor={readBoolField(values, 'doorFinishEdgeColorSameAsDoor', true)}
                  onUseGeneralChange={(useGeneral) => setFieldValue('hasDefaultDoorFinish', useGeneral)}
                  onCustomMaterialChange={(material) => setFieldValue('doorFinishMaterial', material)}
                  onCustomColorChange={(color) => setFieldValue('doorFinishColor', color)}
                  onCustomEdgeColorChange={(color) => setFieldValue('doorFinishEdgeColor', color)}
                  onCustomEdgeColorSameChange={(same) => setFieldValue('doorFinishEdgeColorSameAsDoor', same)}
                  label="portes"
                  open={isDoorDialogOpen}
                  onOpenChange={setIsDoorDialogOpen}
                />
              </div>
            ) : null}

            {drawersIncluded ? (
              <div className="rounded-md border p-3">
                <div className="text-xs font-medium text-muted-foreground">Tiroirs</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Nombre de tiroirs</Label>
                    <DecimalInput
                      className={'h-8 ' + (isMissing('drawerCount') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={typeof values.drawerCount === 'string' ? values.drawerCount : values.drawerCount == null ? '' : String(values.drawerCount)}
                      onValueChange={(next) => setFieldValue('drawerCount', next === '' ? undefined : next)}
                      allowNegative={false}
                    />
                    {isMissing('drawerCount') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Couleur coulisses</Label>
                    <Input
                      className={'h-8 ' + (isMissing('drawerSlideColor') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={typeof values.drawerSlideColor === 'string' ? values.drawerSlideColor : values.drawerSlideColor == null ? '' : String(values.drawerSlideColor)}
                      onChange={(e) => setFieldValue('drawerSlideColor', e.target.value || undefined)}
                    />
                    {isMissing('drawerSlideColor') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Hauteurs</Label>
                    <Input
                      className={'h-8 ' + (isMissing('drawerSpecifications') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={typeof values.drawerSpecifications === 'string' ? values.drawerSpecifications : values.drawerSpecifications == null ? '' : String(values.drawerSpecifications)}
                      onChange={(e) => setFieldValue('drawerSpecifications', e.target.value || undefined)}
                    />
                    {isMissing('drawerSpecifications') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-muted-foreground">Finition</div>
                      <ModeBadge mode={hasDefaultDrawerFinish ? 'default' : 'custom'} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {defaultsLoading ? (
                        'Chargement des finitions de base…'
                      ) : hasDefaultDrawerFinish ? (
                        defaultsFacade ? `${defaultsFacade.material || ''}${defaultsFacade.color ? ` - ${defaultsFacade.color}` : ''}` :
                        'Finition de base (devis)'
                      ) : (
                        formatFacadePreview(values, 'drawerFinish') || 'Spécifique'
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => {
                        setIsDrawerDialogOpen(true)
                      }}
                    >
                      Modifier…
                    </Button>
                  </div>
                </div>

                <FacadeFinishSection
                  generalFinish={defaultsFacade ?? undefined}
                  useGeneral={hasDefaultDrawerFinish}
                  customMaterial={readStringField(values, 'drawerFinishMaterial')}
                  customColor={readStringField(values, 'drawerFinishColor')}
                  customEdgeColor={readStringField(values, 'drawerFinishEdgeColor')}
                  customEdgeColorSameAsDoor={readBoolField(values, 'drawerFinishEdgeColorSameAsDoor', true)}
                  onUseGeneralChange={(useGeneral) => setFieldValue('hasDefaultDrawerFinish', useGeneral)}
                  onCustomMaterialChange={(material) => setFieldValue('drawerFinishMaterial', material)}
                  onCustomColorChange={(color) => setFieldValue('drawerFinishColor', color)}
                  onCustomEdgeColorChange={(color) => setFieldValue('drawerFinishEdgeColor', color)}
                  onCustomEdgeColorSameChange={(same) => setFieldValue('drawerFinishEdgeColorSameAsDoor', same)}
                  label="tiroirs"
                  open={isDrawerDialogOpen}
                  onOpenChange={setIsDrawerDialogOpen}
                />
              </div>
            ) : null}

            {interiorDrawersIncluded ? (
              <div className="rounded-md border p-3">
                <div className="text-xs font-medium text-muted-foreground">Tiroirs intérieurs</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <DecimalInput
                      className={'h-8 ' + (isMissing('interiorDrawerCount') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={
                        typeof values.interiorDrawerCount === 'string'
                          ? values.interiorDrawerCount
                          : values.interiorDrawerCount == null
                            ? ''
                            : String(values.interiorDrawerCount)
                      }
                      onValueChange={(next) => setFieldValue('interiorDrawerCount', next === '' ? undefined : next)}
                      allowNegative={false}
                    />
                    {isMissing('interiorDrawerCount') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>

                  <div className="space-y-1 ">
                    <Label className="text-xs text-muted-foreground">Hauteurs </Label>
                    <Input
                      className={'h-8 ' + (isMissing('interiorDrawerSpecifications') ? 'border-amber-400 focus-visible:ring-amber-300' : '')}
                      value={
                        typeof values.interiorDrawerSpecifications === 'string'
                          ? values.interiorDrawerSpecifications
                          : values.interiorDrawerSpecifications == null
                            ? ''
                            : String(values.interiorDrawerSpecifications)
                      }
                      onChange={(e) => setFieldValue('interiorDrawerSpecifications', e.target.value || undefined)}
                    />
                    {isMissing('interiorDrawerSpecifications') ? <div className="text-xs text-amber-800">Champ requis.</div> : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-4">
              <div className="rounded-md border p-3 md:col-span-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-muted-foreground">Finition caisse</div>
                    <ModeBadge mode={hasDefaultCarcassFinish ? 'default' : 'custom'} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {defaultsLoading ? (
                      'Chargement des finitions de base…'
                    ) : hasDefaultCarcassFinish ? (
                      defaultsCarcass
                        ? `${defaultsCarcass.finish || ''}${defaultsCarcass.carcassColor ? ` - ${defaultsCarcass.carcassColor}` : ''}`
                        : 'Finition de base (devis)'
                    ) : (
                      formatCarcassPreview(values) || 'Spécifique'
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-xs"
                    onClick={() => {
                      setIsCarcassDialogOpen(true)
                    }}
                  >
                    Modifier…
                  </Button>
                </div>

                <CarcassFinishSection
                  generalFinish={defaultsCarcass ?? undefined}
                  useGeneral={hasDefaultCarcassFinish}
                  customFinish={readStringField(values, 'carcassFinishFinish')}
                  customColor={readStringField(values, 'carcassFinishColor')}
                  customEdgeColor={readStringField(values, 'carcassFinishEdgeColor')}
                  customEdgeColorSameAsDoor={readBoolField(values, 'carcassFinishEdgeColorSameAsDoor', true)}
                  customSideViewFinish={readStringField(values, 'carcassFinishSideViewFinish')}
                  customSideViewFinishSameAsDoor={readBoolField(values, 'carcassFinishSideViewFinishSameAsDoor', true)}
                  onUseGeneralChange={(useGeneral) => setFieldValue('hasDefaultCarcassFinish', useGeneral)}
                  onCustomFinishChange={(finish) => setFieldValue('carcassFinishFinish', finish)}
                  onCustomColorChange={(color) => setFieldValue('carcassFinishColor', color)}
                  onCustomEdgeColorChange={(color) => setFieldValue('carcassFinishEdgeColor', color)}
                  onCustomEdgeColorSameChange={(same) => setFieldValue('carcassFinishEdgeColorSameAsDoor', same)}
                  onCustomSideViewFinishChange={(finish) => setFieldValue('carcassFinishSideViewFinish', finish)}
                  onCustomSideViewFinishSameChange={(same) => setFieldValue('carcassFinishSideViewFinishSameAsDoor', same)}
                  open={isCarcassDialogOpen}
                  onOpenChange={setIsCarcassDialogOpen}
                />
              </div>

              {handlesIncluded ? (
                <div className="rounded-md border p-3 md:col-span-1">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-muted-foreground">Poignées</div>
                      <ModeBadge mode={hasDefaultHandlesFinish ? 'default' : 'custom'} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {defaultsLoading ? (
                        'Chargement des finitions de base…'
                      ) : hasDefaultHandlesFinish ? (
                        defaultsHandles ? `${defaultsHandles.model || ''}${defaultsHandles.color ? ` - ${defaultsHandles.color}` : ''}` : 'Finition de base (devis)'
                      ) : (
                        formatHandlesPreview(values) || 'Spécifique'
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => {
                        setIsHandlesDialogOpen(true)
                      }}
                    >
                      Modifier…
                    </Button>
                  </div>

                  <HandlesFinishSection
                    generalFinish={defaultsHandles ?? undefined}
                    useGeneral={hasDefaultHandlesFinish}
                    customModel={readStringField(values, 'handlesFinishModel')}
                    customColor={readStringField(values, 'handlesFinishColor')}
                    onUseGeneralChange={(useGeneral) => setFieldValue('hasDefaultHandlesFinish', useGeneral)}
                    onCustomModelChange={(model) => setFieldValue('handlesFinishModel', model)}
                    onCustomColorChange={(color) => setFieldValue('handlesFinishColor', color)}
                    open={isHandlesDialogOpen}
                    onOpenChange={setIsHandlesDialogOpen}
                  />
                </div>
              ) : null}

              {showPlinth && plinthIncluded ? (
                <div className="rounded-md border p-3 md:col-span-1">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-muted-foreground">Socle</div>
                      <ModeBadge mode={hasDefaultPlinthFinish ? 'default' : 'custom'} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {defaultsLoading ? (
                        'Chargement des finitions de base…'
                      ) : hasDefaultPlinthFinish ? (
                        defaultsPlinth
                          ? formatPlinthPreviewFromFields(
                              false,
                              defaultsPlinth.material ?? '',
                              defaultsPlinth.colorSameAsDoor,
                              defaultsPlinth.color ?? '',
                              typeof defaultsPlinth.height === 'number' ? defaultsPlinth.height : null
                            )
                          : 'Finition de base (devis)'
                      ) : (
                        formatPlinthPreviewFromFields(
                          readBoolField(values, 'plinthCustomMaterialSameAsDoor', true),
                          readStringField(values, 'plinthCustomMaterial'),
                          readBoolField(values, 'plinthCustomColorSameAsDoor', true),
                          readStringField(values, 'plinthCustomColor'),
                          readNumberField(values, 'plinthCustomHeight')
                        ) || 'Spécifique'
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => {
                        setIsPlinthDialogOpen(true)
                      }}
                    >
                      Modifier…
                    </Button>
                  </div>

                  <PlinthFinishSection
                    generalFinish={defaultsPlinth ?? undefined}
                    useGeneral={hasDefaultPlinthFinish}
                    customMaterial={readStringField(values, 'plinthCustomMaterial')}
                    customMaterialSameAsDoor={readBoolField(values, 'plinthCustomMaterialSameAsDoor', true)}
                    customColor={readStringField(values, 'plinthCustomColor')}
                    customColorSameAsDoor={readBoolField(values, 'plinthCustomColorSameAsDoor', true)}
                    customHeight={readNumberField(values, 'plinthCustomHeight') ?? undefined}
                    onUseGeneralChange={(useGeneral) => setFieldValue('hasDefaultPlinthFinish', useGeneral)}
                    onCustomMaterialChange={(material) => setFieldValue('plinthCustomMaterial', material)}
                    onCustomMaterialSameChange={(same) => setFieldValue('plinthCustomMaterialSameAsDoor', same)}
                    onCustomColorChange={(color) => setFieldValue('plinthCustomColor', color)}
                    onCustomColorSameChange={(same) => setFieldValue('plinthCustomColorSameAsDoor', same)}
                    onCustomHeightChange={(height) => setFieldValue('plinthCustomHeight', height)}
                    open={isPlinthDialogOpen}
                    onOpenChange={setIsPlinthDialogOpen}
                  />

                  {!hasDefaultPlinthFinish && effectiveMissingRequiredFieldIds.some((id) => String(id).startsWith('plinthCustom')) ? (
                    <div className="mt-2 text-xs text-amber-800">Champ requis.</div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* <div className="rounded-md border p-3"> */}
              {/* <div className="text-xs font-medium text-muted-foreground">Références & note</div> */}
              {/* <div className="mt-2 grid grid-cols-1 gap-y-3"> */}
                {/* <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Référence fournisseur</Label>
                  <Input
                    className="h-8"
                    value={
                      typeof values.supplierReference === 'string'
                        ? values.supplierReference
                        : values.supplierReference == null
                          ? ''
                          : String(values.supplierReference)
                    }
                    onChange={(e) => setFieldValue('supplierReference', e.target.value || undefined)}
                  />
                </div> */}

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Note complémentaire</Label>
                  <Textarea
                    className="min-h-[70px]"
                    value={
                      typeof values.additionalNote === 'string'
                        ? values.additionalNote
                        : values.additionalNote == null
                          ? ''
                          : String(values.additionalNote)
                    }
                    onChange={(e) => setFieldValue('additionalNote', e.target.value || undefined)}
                  />
                </div>
              {/* </div>
            </div> */}

            <FamilyEditorPricingSection
              schema={schema}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              unitPrice={unitPrice}
              onUnitPriceChange={onUnitPriceChange}
              vatPercent={vatPercent}
              onVatPercentChange={onVatPercentChange}
              priceIncluded={priceIncluded}
              onPriceIncludedChange={onPriceIncludedChange}
            />
          </div>
        </div>
      )}
    </div>
  )
}
